import { analyzeCategory } from './categoryClassifier';

const normalizeText = (value = '') => String(value || '').trim();

const FACT_TYPES = Object.freeze({
  EVENT: 'event',
  STANDING_FACT: 'standing_fact',
  GOAL: 'goal',
  HISTORY_PROGRESS: 'history_progress',
  REFERENCE_GUIDANCE: 'reference_guidance',
  AMBIGUOUS: 'ambiguous',
});

const LEGACY_CATEGORIES = new Set([
  'behavior',
  'medication',
  'food',
  'sleep',
  'mood',
  'activity',
  'milestone',
]);

const PATTERNS = {
  goal: [
    /\bgoal(s)?\b/i,
    /\bobjective(s)?\b/i,
    /\btarget(s)?\b/i,
    /\bsuccess criteria\b/i,
    /\bplan to\b/i,
  ],
  standing_fact: [
    /\ballerg(y|ies|ic)\b/i,
    /\bdiagnosis\b/i,
    /\bpast medical history\b/i,
    /\bmedical history\b/i,
    /\bproblem list\b/i,
    /\bcurrent med(s|ications?)\b/i,
    /\bprescribed\b/i,
    /\bbaseline\b/i,
    /\bcondition\b/i,
  ],
  history_progress: [
    /\bprogress\b/i,
    /\bhospital course\b/i,
    /\bimprov(?:ed|ing|ement)\b/i,
    /\bworsen(?:ed|ing)?\b/i,
    /\bdeclin(?:ed|ing|e)\b/i,
    /\bdischarge\b/i,
    /\brecovery\b/i,
    /\brecovered\b/i,
    /\btransition\b/i,
    /\bupdate\b/i,
    /\bsince\b/i,
  ],
  reference_guidance: [
    /\bguidance\b/i,
    /\binstruction(s)?\b/i,
    /\brecommendation(s)?\b/i,
    /\bcare plan\b/i,
    /\bbehavior support plan\b/i,
    /\bsafety plan\b/i,
    /\bfeeding plan\b/i,
    /\baccommodation(s)?\b/i,
    /\bstrategy\b/i,
    /\bhow to\b/i,
    /\buse\s+(?:aac|visual schedule|extra time)\b/i,
  ],
  event: [
    /\bincident\b/i,
    /\bevent\b/i,
    /\boccurred\b/i,
    /\bhappened\b/i,
    /\badmitted\b/i,
    /\bcalled\b/i,
    /\bmet\b/i,
    /\breported\b/i,
    /\bobserved\b/i,
    /\bgave\b/i,
    /\btook\b/i,
    /\bstarted\b/i,
    /\bstopped\b/i,
    /\bbegan\b/i,
    /\bended\b/i,
  ],
};

const FACT_TYPE_ORDER = [
  FACT_TYPES.GOAL,
  FACT_TYPES.REFERENCE_GUIDANCE,
  FACT_TYPES.HISTORY_PROGRESS,
  FACT_TYPES.STANDING_FACT,
  FACT_TYPES.EVENT,
];

const buildText = (candidate = {}) => [
  candidate.sourceText,
  candidate.label,
  candidate.extracted?.sourceText,
  candidate.extracted?.body,
  candidate.extracted?.description,
  candidate.extracted?.title,
  candidate.extracted?.findingCollection,
  candidate.extracted?.findingKind,
].map(normalizeText).filter(Boolean).join('\n');

const addSignal = (scores, signals, factType, weight, signal) => {
  scores[factType] = (scores[factType] || 0) + weight;
  if (signal) {
    signals.push(signal);
  }
};

const hasPattern = (text, patterns) => patterns.some((pattern) => pattern.test(text));

const addWeakLegacyCategoryBoost = (scores, signals, candidate = {}, text = '') => {
  const analysis = analyzeCategory(text, { allowUnknown: true });
  if (!LEGACY_CATEGORIES.has(analysis.category)) {
    return;
  }

  signals.push(`legacy_category:${analysis.category}`);
  if (scores.event > 0) {
    addSignal(scores, signals, FACT_TYPES.EVENT, 1, `legacy_category_boost:${analysis.category}`);
    return;
  }
  if (scores.history_progress > 0) {
    addSignal(scores, signals, FACT_TYPES.HISTORY_PROGRESS, 1, `legacy_category_boost:${analysis.category}`);
    return;
  }
  if (scores.reference_guidance > 0) {
    addSignal(scores, signals, FACT_TYPES.REFERENCE_GUIDANCE, 1, `legacy_category_boost:${analysis.category}`);
    return;
  }
  if (scores.standing_fact > 0) {
    addSignal(scores, signals, FACT_TYPES.STANDING_FACT, 1, `legacy_category_boost:${analysis.category}`);
  }
};

const scoreCandidateFactType = (candidate = {}) => {
  const text = buildText(candidate);
  const purposeType = normalizeText(candidate.documentPurpose?.type || candidate.origin?.metadata?.documentPurpose?.type).toLowerCase();
  const findingCollection = normalizeText(candidate.findingMetadata?.findingCollection || candidate.payload?.findingCollection).toLowerCase();
  const findingKind = normalizeText(candidate.findingMetadata?.findingKind || candidate.payload?.findingKind).toLowerCase();
  const extractedType = normalizeText(candidate.findingMetadata?.extractedType || candidate.payload?.extractedType).toLowerCase();
  const signals = [];
  const scores = {
    [FACT_TYPES.EVENT]: 0,
    [FACT_TYPES.STANDING_FACT]: 0,
    [FACT_TYPES.GOAL]: 0,
    [FACT_TYPES.HISTORY_PROGRESS]: 0,
    [FACT_TYPES.REFERENCE_GUIDANCE]: 0,
  };

  if (findingKind === 'goal' || extractedType === 'goal_detail') {
    addSignal(scores, signals, FACT_TYPES.GOAL, 4, 'finding_kind:goal');
  }

  if (findingKind === 'allergy' || extractedType === 'allergy_detail') {
    addSignal(scores, signals, FACT_TYPES.STANDING_FACT, 4, 'finding_kind:allergy');
  }

  if (findingCollection === 'dates') {
    addSignal(scores, signals, FACT_TYPES.EVENT, 1, 'finding_collection:dates');
  }

  if (hasPattern(text, PATTERNS.goal)) {
    addSignal(scores, signals, FACT_TYPES.GOAL, 3, 'goal_pattern');
  }

  if (hasPattern(text, PATTERNS.standing_fact)) {
    addSignal(scores, signals, FACT_TYPES.STANDING_FACT, 3, 'standing_fact_pattern');
  }

  if (hasPattern(text, PATTERNS.history_progress)) {
    addSignal(scores, signals, FACT_TYPES.HISTORY_PROGRESS, 3, 'history_progress_pattern');
  }

  if (hasPattern(text, PATTERNS.reference_guidance)) {
    addSignal(scores, signals, FACT_TYPES.REFERENCE_GUIDANCE, 3, 'reference_guidance_pattern');
  }

  if (hasPattern(text, PATTERNS.event)) {
    addSignal(scores, signals, FACT_TYPES.EVENT, 3, 'event_pattern');
  }

  if (purposeType === 'discharge_hospital_summary' && scores.history_progress > 0) {
    addSignal(scores, signals, FACT_TYPES.HISTORY_PROGRESS, 1, 'document_purpose:discharge_hospital_summary');
  }

  if (purposeType === 'plan_guidance_care_reference' && scores.reference_guidance > 0) {
    addSignal(scores, signals, FACT_TYPES.REFERENCE_GUIDANCE, 1, 'document_purpose:plan_guidance_care_reference');
  }

  if (purposeType === 'iep_ard_school_plan') {
    if (scores.goal > 0) {
      addSignal(scores, signals, FACT_TYPES.GOAL, 1, 'document_purpose:iep_ard_school_plan');
    }
    if (scores.reference_guidance > 0) {
      addSignal(scores, signals, FACT_TYPES.REFERENCE_GUIDANCE, 1, 'document_purpose:iep_ard_school_plan');
    }
  }

  if (purposeType === 'crisis_incident_report' && scores.event > 0) {
    addSignal(scores, signals, FACT_TYPES.EVENT, 1, 'document_purpose:crisis_incident_report');
  }

  addWeakLegacyCategoryBoost(scores, signals, candidate, text);

  return { scores, signals, text };
};

const chooseFactType = (scores = {}) => {
  const ordered = FACT_TYPE_ORDER
    .map((factType) => ({ factType, score: scores[factType] || 0 }))
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score;
      }
      return FACT_TYPE_ORDER.indexOf(left.factType) - FACT_TYPE_ORDER.indexOf(right.factType);
    });

  const [winner, runnerUp] = ordered;
  if (!winner || winner.score < 2) {
    return {
      factType: FACT_TYPES.AMBIGUOUS,
      confidence: 'low',
      winner,
      runnerUp,
    };
  }

  const scoreGap = winner.score - (runnerUp?.score || 0);
  const confidence = winner.score >= 5 && scoreGap >= 2
    ? 'high'
    : winner.score >= 3
      ? 'medium'
      : 'low';

  return {
    factType: winner.factType,
    confidence,
    winner,
    runnerUp,
  };
};

export const buildTypedDocumentCandidate = (candidate = {}) => {
  const { scores, signals } = scoreCandidateFactType(candidate);
  const selection = chooseFactType(scores);
  const typedKey = candidate.key
    ? `${candidate.key}::${selection.factType}`
    : '';

  return {
    schemaVersion: 1,
    key: typedKey,
    sourceCandidateKey: candidate.key || '',
    origin: candidate.origin,
    domain: 'document_fact',
    factType: selection.factType,
    factTypeConfidence: selection.confidence,
    factTypeSignals: signals,
    extractionConfidence: candidate.confidence || 'low',
    documentPurpose: candidate.documentPurpose || candidate.origin?.metadata?.documentPurpose || null,
    sourceText: candidate.sourceText || '',
    extracted: candidate.extracted || {},
    provenance: candidate.provenance || candidate.findingMetadata || {},
    findingMetadata: candidate.findingMetadata || candidate.payload || {},
  };
};

export const buildTypedDocumentCandidatesFromNeutralCandidates = (candidates = []) => (
  Array.isArray(candidates)
    ? candidates.map((candidate) => buildTypedDocumentCandidate(candidate)).filter(Boolean)
    : []
);

export const FACT_TYPE_VALUES = FACT_TYPES;

export default buildTypedDocumentCandidatesFromNeutralCandidates;
